import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { Add as AddIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import Layout from '../components/Layout';
import ResponsiveTable from '../components/ResponsiveTable';
import SelectField from '../components/inputs/SelectField';
import SpecialRequestFormFields from '../components/specialRequests/SpecialRequestFormFields';
import SpecialRequestDetailView from '../components/specialRequests/SpecialRequestDetailView';
import { useFeedback } from '../context/FeedbackContext';
import { useAsyncAction } from '../hooks/useAsyncAction';
import { ALL_SPECIAL_REQUEST_CATEGORIES } from '../constants/appConstants';
import { formatDateTime } from '../utils/dateFormat';
import {
  DEFAULT_SPECIAL_REQUEST_FORM,
  buildSpecialRequestPayload,
  validateSpecialRequestForm,
  buildOrderDescriptionFromRequest,
  formatSpecialRequestOrderRef,
  getSpecialRequestOrderId,
} from '../utils/specialRequestDisplay';

function getStatusColor(status) {
  switch (status) {
    case 'submitted': return 'default';
    case 'inProgress': return 'info';
    case 'ready': return 'success';
    case 'denied': return 'error';
    case 'fulfilled': return 'success';
    default: return 'default';
  }
}

export default function SpecialRequests() {
  const { t } = useTranslation();
  const { showError, showSuccess } = useFeedback();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({ ...DEFAULT_SPECIAL_REQUEST_FORM });
  const [filterOptions, setFilterOptions] = useState({ shapes: [], colors: [], clarities: [] });
  const { loading: saving, run: runSave } = useAsyncAction();

  const categoryOptions = useMemo(() => ALL_SPECIAL_REQUEST_CATEGORIES.map((value) => ({
    value,
    label: t(`specialRequests.categories.${value}`),
  })), [t]);

  const fetchFilterOptions = useCallback(async () => {
    try {
      const res = await axios.get('/api/diamonds/filters/options');
      setFilterOptions({
        shapes: res.data?.certifiedShapes || res.data?.shapes || [],
        certifiedShapes: res.data?.certifiedShapes || res.data?.shapes || [],
        lotShapes: res.data?.lotShapes || [],
        colors: res.data?.colors || [],
        clarities: res.data?.clarities || [],
      });
    } catch {
      setFilterOptions({ shapes: [], colors: [], clarities: [] });
    }
  }, []);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/special-requests');
      setRequests(res.data.specialRequests || []);
    } catch (err) {
      showError(err.response?.data?.error || t('common.error'));
    } finally {
      setLoading(false);
    }
  }, [showError, t]);

  useEffect(() => {
    fetchFilterOptions();
    fetchRequests();
  }, [fetchFilterOptions, fetchRequests]);

  const resetForm = () => {
    setForm({ ...DEFAULT_SPECIAL_REQUEST_FORM, details: { ...DEFAULT_SPECIAL_REQUEST_FORM.details } });
  };

  const handleCreate = async () => {
    const validationError = validateSpecialRequestForm(form, t);
    if (validationError) {
      showError(validationError);
      return;
    }
    await runSave(async () => {
      try {
        await axios.post('/api/special-requests', buildSpecialRequestPayload(form));
        showSuccess(t('specialRequests.created'));
        setCreateOpen(false);
        resetForm();
        await fetchRequests();
      } catch (err) {
        showError(err.response?.data?.error || t('common.error'));
      }
    });
  };

  const columns = [
    { id: 'createdAt', label: t('orders.date'), render: (row) => formatDateTime(row.createdAt) },
    { id: 'category', label: t('specialRequests.category'), render: (row) => t(`specialRequests.categories.${row.category}`) },
    {
      id: 'status',
      label: t('orders.status'),
      render: (row) => (
        <Chip
          size="small"
          label={t(`specialRequests.statuses.${row.status}`)}
          color={getStatusColor(row.status)}
        />
      ),
    },
    {
      id: 'order',
      label: t('specialRequests.linkedOrder'),
      render: (row) => {
        const orderId = getSpecialRequestOrderId(row);
        if (!orderId) return '—';
        return (
          <Typography
            component={Link}
            to={`/orders/${orderId}`}
            variant="body2"
            sx={{ fontWeight: 600, textDecoration: 'none', color: 'primary.main' }}
            onClick={(e) => e.stopPropagation()}
          >
            #{formatSpecialRequestOrderRef(row.convertedOrder)}
          </Typography>
        );
      },
    },
    {
      id: 'description',
      label: t('specialRequests.summary'),
      render: (row) => (
        <Typography
          variant="body2"
          noWrap
          sx={{
            maxWidth: 280,
            cursor: 'pointer',
            textDecoration: 'underline',
            textDecorationColor: 'divider',
          }}
          onClick={(e) => {
            e.stopPropagation();
            setDetail(row);
          }}
        >
          {buildOrderDescriptionFromRequest(row)}
        </Typography>
      ),
    },
  ];

  return (
    <Layout>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" fontWeight={700}>{t('specialRequests.title')}</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)} size="small">
          {t('specialRequests.create')}
        </Button>
      </Box>

      <ResponsiveTable
        columns={columns}
        data={requests}
        loading={loading}
        emptyMessage={t('specialRequests.empty')}
        onRowClick={(row) => setDetail(row)}
        renderMobileCard={(row) => (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
                {formatDateTime(row.createdAt)}
              </Typography>
              <Chip
                size="small"
                label={t(`specialRequests.statuses.${row.status}`)}
                color={getStatusColor(row.status)}
                sx={{ flexShrink: 0 }}
              />
            </Box>
            <Typography variant="subtitle2" fontWeight={600}>
              {t(`specialRequests.categories.${row.category}`)}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.5, wordBreak: 'break-word', whiteSpace: 'normal' }}
            >
              {buildOrderDescriptionFromRequest(row)}
            </Typography>
            <Typography variant="caption" color="primary.main" sx={{ mt: 1, display: 'block' }}>
              {t('specialRequests.viewDetails')}
            </Typography>
          </>
        )}
      />

      <Dialog open={createOpen} onClose={() => { setCreateOpen(false); resetForm(); }} maxWidth="sm" fullWidth>
        <DialogTitle>{t('specialRequests.createTitle')}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <SelectField
              label={t('specialRequests.category')}
              value={form.category}
              onChange={(value) => setForm({ ...DEFAULT_SPECIAL_REQUEST_FORM, category: value, details: { ...DEFAULT_SPECIAL_REQUEST_FORM.details } })}
              options={categoryOptions}
              size="small"
            />
            <SpecialRequestFormFields
              form={form}
              onChange={setForm}
              filterOptions={filterOptions}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setCreateOpen(false); resetForm(); }} disabled={saving}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleCreate} disabled={saving}>
            {saving ? t('common.saving') : t('specialRequests.submit')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(detail)} onClose={() => setDetail(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('specialRequests.detailTitle')}</DialogTitle>
        <DialogContent dividers>
          <SpecialRequestDetailView request={detail} getStatusColor={getStatusColor} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetail(null)}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}
